import { type FunctionComponent, useCallback } from 'react';

import { NavPages } from '../components/nav/navPages/NavPagesConnected';
import { useAudioTrigger } from '../controllers/audioController/hooks';
import { useGameRedirectConnected } from '../hooks/useGameRedirectConnected';
import { useCoreDispatch } from '../store/CoreStore';
import { useConfigSlice } from '../store/slices/config/selectors';
import { headerActions } from '../store/slices/header/slice';
import { useMiniLobbySlice } from '../store/slices/minilobby/selectors';
import { miniLobbyActions } from '../store/slices/minilobby/slice';
import { settingsActions } from '../store/slices/settings/slice';
import { useIsLargerThanWide } from '../styles/hooks/media';
import { getWindowObject } from '../utils/window';

import { CoreLobbyMessageController, tableInfoMessage } from './CoreLobbyMessageController';
import { CoreLobbyMessageControllerV2, hostInfoMessage } from './CoreLobbyMessageControllerV2';
import { MessageReceiver } from './MessageReceiver';
import type { SoundMessagePayload } from './messages/sound/types';
import type { IncomingMessage } from './types';

export const LobbyMessageReceiver: FunctionComponent = () => {
  const dispatch = useCoreDispatch();
  const tableId = useConfigSlice.session()?.tableId;
  const audioTrigger = useAudioTrigger();
  const isLobbyFullscreen = useMiniLobbySlice.getIsLobbyFullscreen();
  const gameRedirect = useGameRedirectConnected();
  const isWideScreen = useIsLargerThanWide();
  const windowObject = getWindowObject();

  const onLobbyBackClick = useCallback((): void => {
    if (!isWideScreen) dispatch(headerActions.pushNavRoute(NavPages.HOME));
  }, [dispatch, isWideScreen]);

  const handleMiniLobbyMessage = useCallback(
    (message: IncomingMessage): void => {
      if (!tableId) return;

      // legacy messages
      if (message?.type) {
        switch (message.type) {
          // V1 messages
          // Handle initial messages on iframe load. All three require post response with tableId to display Current Table badge.
          case 'notifyfsloaded_pp': // First message
          case 'notifyoperatorId': // Second message
            CoreLobbyMessageController.post(
              tableInfoMessage({
                type: 'tableInfo',
                tableId,
              })
            );

            break;

          case 'pp_get_tableId': // Third message
            CoreLobbyMessageController.post(
              tableInfoMessage({
                type: 'tableInfo',
                tableId,
              })
            );
            dispatch(miniLobbyActions.setIsIframeLoaded(true));
            break;

          // Close lobby mobile
          case 'closeLobbyMenu':
            dispatch(headerActions.setMiniLobbyOpen(false));
            dispatch(headerActions.setOpenedModal(null));
            dispatch(headerActions.setNavRoute([]));
            audioTrigger.play({ name: 'menuClick' });
            break;

          // Close lobby desktop
          case 'hideLobbyIframe':
            if (isLobbyFullscreen) {
              dispatch(miniLobbyActions.setIsLobbyFullscreen(false));
            }
            dispatch(headerActions.setNavRoute([]));
            dispatch(headerActions.setMiniLobbyOpen(false));
            audioTrigger.play({ name: 'menuClick' });
            onLobbyBackClick();
            break;

          // Redirect to live game
          case 'pp_redirect_game': {
            const tableType = message.launchGameType ?? message.tableType;

            if (!tableType || !message.urlMap) return;

            gameRedirect({
              tableType: tableType as string,
              urlMap: message.urlMap,
            });

            break;
          }

          // Redirect to slot game
          case 'pp_slots_loadGame': {
            if (!message.path || typeof message.path !== 'string') return;

            windowObject.location.assign(message.path);
            break;
          }

          // Expand or minimize lobby
          case 'toggleLobbyIframe':
            dispatch(miniLobbyActions.setIsLobbyFullscreen(!message.visible));
            break;

          default:
            break;
        }
      } else if (message?.name && message?.group === 'lobby') {
        switch (message.name) {
          case 'toggleOverlay': //V2
          case 'toggleBackToGame': {
            const payload = message?.payload as { isHidden: boolean };
            dispatch(headerActions.setBackToGameHidden(payload.isHidden));
            break;
          }

          // V2 messages
          case 'readyToReceive': {
            CoreLobbyMessageControllerV2.post(
              // Check message host value
              hostInfoMessage({ id: tableId, host: 'liveCasino' })
            );
            dispatch(miniLobbyActions.setIsIframeLoaded(true));
            break;
          }

          case 'open': {
            const payload = message?.payload as { url: string } | undefined;
            const url = payload?.url;
            if (!url) return;
            window.open(url, '_self');
            break;
          }

          case 'sound': {
            const payload = message?.payload as SoundMessagePayload | undefined;
            if (!payload) return;
            const { masterVolume, channels } = payload;
            dispatch(settingsActions.setMasterVolumeOnly(masterVolume));
            if (channels) {
              channels.forEach((channel) => {
                dispatch(
                  settingsActions.setVolume({
                    channel: channel.name,
                    value: channel.volume,
                  })
                );
              });
            }
            break;
          }

          case 'mute': {
            dispatch(settingsActions.mute());
            break;
          }

          case 'unmute': {
            dispatch(settingsActions.unmute());
            break;
          }

          default:
            break;
        }
      }
    },
    [audioTrigger, dispatch, gameRedirect, isLobbyFullscreen, onLobbyBackClick, tableId]
  );

  return <MessageReceiver handler={handleMiniLobbyMessage} />;
};
